// packages/web/src/components/AppShell.tsx — misma lógica (roles, colapsado persistido, drawer móvil).
// Cambios: wordmark serif en navy, "Nueva aula" como botón de acción, sección "Administración"
// separada para system_admin, foco visible, encabezado más liviano.
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Menu, X, LogOut, GraduationCap, Library, TrendingUp, BarChart3, ChevronLeft, ChevronRight,
  Sparkles, ShieldCheck, Users, Plus,
} from 'lucide-react';
import { useCurrentUser } from '../auth/LoginGate';
import type { AuthIdentity } from '../auth/useAuth';

export type ScreenId =
  | 'form' | 'matrix' | 'interview' | 'report' | 'metrics' | 'catalog' | 'learner' | 'dashboard'
  | 'admin-prompts' | 'admin-audit' | 'classroom-nueva' | 'classrooms-list' | 'classroom-live';

interface NavItem {
  id: ScreenId;
  label: string;
  icon: typeof GraduationCap;
  roles?: Array<AuthIdentity['activeRole']>;
  section: 'acciones' | 'analisis' | 'admin';
}

const NAV_ITEMS: readonly NavItem[] = [
  { id: 'classroom-nueva', label: 'Nueva aula', icon: Plus, roles: ['curriculum_admin', 'school_admin', 'system_admin'], section: 'acciones' },
  { id: 'classrooms-list', label: 'Mis aulas', icon: Users, roles: ['curriculum_admin', 'school_admin', 'system_admin'], section: 'analisis' },
  { id: 'dashboard', label: 'Dashboard', icon: TrendingUp, section: 'analisis' },
  { id: 'catalog', label: 'Catálogo OA', icon: Library, roles: ['teacher', 'system_admin'], section: 'analisis' },
  { id: 'metrics', label: 'Métricas y costos', icon: BarChart3, roles: ['system_admin'], section: 'admin' },
  { id: 'admin-prompts', label: 'Prompts del sistema', icon: Sparkles, roles: ['system_admin'], section: 'admin' },
  { id: 'admin-audit', label: 'Auditoría de consultas', icon: ShieldCheck, roles: ['system_admin'], section: 'admin' },
] as const;

// La vista en vivo pertenece a "Mis aulas" para el resaltado del menú.
const PARENT: Partial<Record<ScreenId, ScreenId>> = { 'classroom-live': 'classrooms-list' };

interface AppShellProps {
  activeScreen: ScreenId;
  onNavigate: (id: ScreenId) => void;
  hideChrome?: boolean;
  children: ReactNode;
}

const COLLAPSED_KEY = 'marzano.sidebar_collapsed';
const FOCUS = 'focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-amber-500';

export function AppShell({ activeScreen, onNavigate, hideChrome, children }: AppShellProps) {
  const { identity, logout } = useCurrentUser();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem(COLLAPSED_KEY) === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(COLLAPSED_KEY, collapsed ? '1' : '0'); } catch { /* cosmético */ }
  }, [collapsed]);
  useEffect(() => { setDrawerOpen(false); }, [activeScreen]);
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setDrawerOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  const visibleItems = useMemo(() => {
    if (!identity) return [];
    return NAV_ITEMS.filter((it) => !it.roles || it.roles.includes(identity.activeRole));
  }, [identity]);

  if (hideChrome) return <div className="min-h-screen bg-slate-50">{children}</div>;

  const active = PARENT[activeScreen] ?? activeScreen;
  const initials = identity?.name.split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase() ?? '';
  const sections: { key: NavItem['section']; title: string }[] = [
    { key: 'analisis', title: 'Análisis' },
    { key: 'admin', title: 'Administración' },
  ];
  const newItem = visibleItems.find((i) => i.section === 'acciones');

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[60] focus:rounded-md focus:bg-white focus:px-3 focus:py-2 focus:shadow">Saltar al contenido</a>
      {drawerOpen && <div className="fixed inset-0 bg-slate-900/40 z-40 lg:hidden" onClick={() => setDrawerOpen(false)} aria-hidden />}

      <aside
        aria-label="Menú principal"
        className={
          'fixed top-0 left-0 h-full bg-white border-r border-slate-200 z-50 transform transition-[transform,width] duration-200 ease-out flex flex-col ' +
          (collapsed ? 'w-16' : 'w-64') + ' ' + (drawerOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0')
        }
      >
        <div className={(collapsed ? 'px-3 justify-center' : 'px-5 justify-between') + ' h-[72px] border-b border-slate-100 flex items-center'}>
          {collapsed ? (
            <span className="font-serif text-xl text-marzano-600" aria-label="Colegio Pioneros">P</span>
          ) : (
            <div className="leading-none">
              <div className="text-[9px] tracking-[0.35em] text-slate-600">COLEGIO</div>
              <div className="font-serif text-[22px] tracking-wide text-marzano-600 mt-0.5">PIONEROS</div>
              <div className="text-[11px] text-slate-600 mt-1">Evaluador Marzano IA</div>
            </div>
          )}
          <button type="button" onClick={() => setDrawerOpen(false)} className={`lg:hidden p-1 rounded text-slate-600 hover:text-slate-900 ${FOCUS}`} aria-label="Cerrar menú">
            <X aria-hidden className="w-5 h-5" />
          </button>
        </div>

        <nav className={(collapsed ? 'px-2' : 'px-3') + ' flex-1 overflow-y-auto py-4 space-y-5'}>
          {newItem && (
            <button type="button" onClick={() => onNavigate(newItem.id)} title={collapsed ? newItem.label : undefined} aria-label={newItem.label}
              aria-current={active === newItem.id ? 'page' : undefined}
              className={`w-full flex items-center justify-center gap-2 rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white font-semibold text-sm min-h-[40px] ${collapsed ? 'px-0' : 'px-3'} ${FOCUS}`}>
              <Plus aria-hidden className="w-4 h-4" />{!collapsed && newItem.label}
            </button>
          )}
          {sections.map(({ key, title }) => {
            const items = visibleItems.filter((i) => i.section === key);
            if (items.length === 0) return null;
            return (
              <div key={key}>
                {!collapsed && <div className="text-[11px] uppercase tracking-wider text-slate-600 font-semibold px-3 mb-1.5">{title}</div>}
                <div className="space-y-0.5">
                  {items.map((it) => <NavItemButton key={it.id} item={it} active={it.id === active} collapsed={collapsed} onClick={() => onNavigate(it.id)} />)}
                </div>
              </div>
            );
          })}
        </nav>

        <button type="button" onClick={() => setCollapsed((v) => !v)}
          className={`hidden lg:flex items-center justify-center w-full py-2.5 border-t border-slate-100 text-slate-600 hover:text-marzano-600 hover:bg-slate-50 ${FOCUS}`}
          aria-label={collapsed ? 'Expandir menú' : 'Colapsar menú'} title={collapsed ? 'Expandir menú' : 'Colapsar menú'}>
          {collapsed ? <ChevronRight aria-hidden className="w-4 h-4" /> : <ChevronLeft aria-hidden className="w-4 h-4" />}
        </button>

        {identity && (
          <div className={(collapsed ? 'p-2' : 'p-3') + ' border-t border-slate-100'}>
            <div className={`flex items-center ${collapsed ? 'flex-col gap-2' : 'gap-3'}`}>
              <div className="bg-marzano-50 rounded-full w-9 h-9 flex items-center justify-center shrink-0" title={`${identity.name} · ${ROLE_LABEL[identity.activeRole]}`}>
                <span className="text-marzano-700 font-semibold text-[13px]">{initials}</span>
              </div>
              {!collapsed && (
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-900 truncate">{identity.name}</div>
                  <div className="text-xs text-slate-600 truncate">{ROLE_LABEL[identity.activeRole]}</div>
                </div>
              )}
              <button type="button" onClick={() => void logout()} aria-label="Cerrar sesión" title="Cerrar sesión"
                className={`p-2 rounded-lg text-slate-600 hover:bg-slate-100 hover:text-slate-900 ${FOCUS}`}>
                <LogOut aria-hidden className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </aside>

      <div className={(collapsed ? 'lg:pl-16' : 'lg:pl-64') + ' min-h-screen flex flex-col transition-[padding] duration-200 ease-out'}>
        <header className="lg:hidden bg-white border-b border-slate-200 px-4 h-14 flex items-center gap-3 sticky top-0 z-30">
          <button type="button" onClick={() => setDrawerOpen(true)} className={`p-2 -ml-2 rounded text-slate-700 ${FOCUS}`} aria-label="Abrir menú">
            <Menu aria-hidden className="w-5 h-5" />
          </button>
          <span className="font-serif text-lg tracking-wide text-marzano-600">PIONEROS</span>
        </header>

        <main id="main" className="flex-1 px-4 sm:px-8 py-6 sm:py-8">{children}</main>

        <footer className="px-4 sm:px-8 py-4 text-center text-xs text-slate-600">
          Marzano IA · evaluación pedagógica conversacional · v0.1{identity?.email && <> · {identity.email}</>}
        </footer>
      </div>
    </div>
  );
}

const ROLE_LABEL: Record<AuthIdentity['activeRole'], string> = {
  student: 'Alumno', teacher: 'Docente', guardian: 'Apoderado', curriculum_admin: 'Curriculista', school_admin: 'Admin de sede', system_admin: 'Administrador',
};

function NavItemButton({ item, active, collapsed, onClick }: { item: NavItem; active: boolean; collapsed: boolean; onClick: () => void }) {
  const Icon = item.icon;
  return (
    <button type="button" onClick={onClick} title={collapsed ? item.label : undefined} aria-label={item.label} aria-current={active ? 'page' : undefined}
      className={
        `w-full flex items-center rounded-lg text-sm transition-colors min-h-[40px] ${FOCUS} ` +
        (collapsed ? 'justify-center px-2' : 'gap-3 px-3 text-left') + ' ' +
        (active ? 'bg-marzano-50 text-marzano-700 font-semibold' : 'text-slate-700 hover:bg-slate-100')
      }>
      <Icon aria-hidden className="w-4 h-4 shrink-0" />
      {!collapsed && <span className="truncate">{item.label}</span>}
    </button>
  );
}
